// Field values, comparison and checks for Admin → Settings. The server repeats every check
// (Modules/Settings/SettingInput.cs); these only give instant feedback.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[0-9][0-9 ()-]{6,19}$/;

const isHttpUrl = value => {
  try {
    const url = new URL(value);
    return (
      (url.protocol === 'https:' || url.protocol === 'http:') &&
      Boolean(url.hostname)
    );
  } catch {
    return false;
  }
};

const FORMAT_CHECKS = {
  Email: [
    value => EMAIL_RE.test(value),
    'Enter an email address like name@example.com.',
  ],
  Url: [isHttpUrl, 'Enter a full address starting with https://'],
  Phone: [
    value => PHONE_RE.test(value),
    'Enter a phone number with the country code, e.g. +91 70198 46664.',
  ],
};

const checkText = (field, value, { allowLineBreaks }) => {
  if (value.length > field.maxLength)
    return `Use ${field.maxLength} characters or fewer.`;
  if (!allowLineBreaks && /[\r\n]/.test(value))
    return 'Remove the line breaks.';
  const format = FORMAT_CHECKS[field.format];
  if (format && !format[0](value)) return format[1];
  if (field.pattern) {
    try {
      if (!new RegExp(field.pattern).test(value))
        return field.patternMessage || "This doesn't look right.";
    } catch {
      // A pattern this browser can't compile is left to the server.
    }
  }
  return null;
};

/** An error message for a value about to be saved, or null. */
export function validateField(field, value) {
  switch (field.kind) {
    case 'Toggle':
      return null;
    case 'Number': {
      const text = String(value ?? '').trim();
      const number = Number(text);
      if (!text || Number.isNaN(number)) return 'Enter a number.';
      if (field.wholeNumber && !Number.isInteger(number))
        return 'Enter a whole number.';
      if (
        (field.min != null && number < field.min) ||
        (field.max != null && number > field.max)
      ) {
        return `Enter a number from ${field.min ?? '…'} to ${field.max ?? '…'}.`;
      }
      return null;
    }
    case 'Select':
      return field.required && !value ? 'Choose one of the options.' : null;
    case 'Days':
      return Array.isArray(value) && value.length
        ? null
        : 'Pick at least one day.';
    case 'List': {
      const items = Array.isArray(value) ? value : [];
      if (items.length > field.maxItems)
        return `Add at most ${field.maxItems}.`;
      for (const item of items) {
        const error = checkText(field, item, { allowLineBreaks: false });
        if (error) return `${item}: ${error}`;
      }
      return null;
    }
    default: {
      const text = String(value ?? '').trim();
      if (!text) return field.required ? "This can't be empty." : null;
      return checkText(field, field.uppercase ? text.toUpperCase() : text, {
        allowLineBreaks: field.kind === 'TextArea',
      });
    }
  }
}

/** The value as the API expects it. */
export function toPayload(field, value) {
  switch (field.kind) {
    case 'Number':
      return Number(String(value).trim());
    case 'Toggle':
      return Boolean(value);
    case 'List':
    case 'Days':
      return Array.isArray(value) ? value : [];
    default:
      return value == null ? '' : String(value);
  }
}

/** True when an edited value is the same as the one in effect (so it isn't a change). */
export function sameAsCurrent(field, value) {
  const current = field.value;
  if (field.kind === 'List' || field.kind === 'Days') {
    const a = Array.isArray(value) ? value : [];
    const b = Array.isArray(current) ? current : [];
    const norm = list =>
      field.kind === 'Days'
        ? [...list].map(Number).sort((x, y) => x - y)
        : list;
    return JSON.stringify(norm(a)) === JSON.stringify(norm(b));
  }
  if (field.kind === 'Number')
    return String(value).trim() !== '' && Number(value) === Number(current);
  if (field.kind === 'Toggle') return Boolean(value) === Boolean(current);
  return String(value ?? '') === String(current ?? '');
}

/** Current value of a field in a loaded section, with this section's unsaved edit if there is one. */
export const valueOf = (section, key, draft = {}) => {
  if (Object.prototype.hasOwnProperty.call(draft, key)) return draft[key];
  return section?.fields.find(f => f.key === key)?.value;
};

const ON = { tone: 'success', label: 'On' };
const OFF = { tone: 'muted', label: 'Off' };
const SET_UP = { tone: 'warning', label: 'Set up' };

/** The small state shown next to a section in the settings menu, or null. */
export function sectionState(section, status) {
  const method = key => status?.paymentMethods?.find(m => m.key === key);
  const methodState = m => (!m?.enabled ? OFF : m.offered ? ON : SET_UP);
  switch (section.id) {
    case 'upi':
      return methodState(method('manual-upi'));
    case 'phonepe':
      return methodState(method('phonepe'));
    case 'invoice':
      return !status?.invoicesEnabled
        ? OFF
        : status.invoiceProblem
          ? SET_UP
          : ON;
    case 'email':
      return status?.emailReady
        ? ON
        : valueOf(section, 'Email:Enabled')
          ? SET_UP
          : OFF;
    case 'github': {
      if (!valueOf(section, 'GitHubSettings:Enabled')) return OFF;
      const token = section.fields.find(
        f => f.key === 'GitHubSettings:AccessToken'
      );
      return token?.isSet ? ON : SET_UP;
    }
    default:
      return null;
  }
}

/** Sections whose values the public site shows; saving them refreshes this browser's copy. */
export const SITE_SECTIONS = ['business', 'hours', 'seo', 'home'];
